/**
 * useVouchers.ts — API hooks for the Voucher / GL ledger explorer.
 *
 * Targets the post-Sprint-2-3 ledger surface:
 *   GET /api/v1/ledger/vouchers         (list)
 *   GET /api/v1/ledger/vouchers/:id     (detail with VoucherLines)
 *   GET /api/v1/ledger/:accountId       (account ledger drill-down)
 *   GET /api/v1/ledger/reports/trial-balance
 *   GET /api/v1/ledger/reports/profit-loss
 *   GET /api/v1/ledger/reports/balance-sheet
 *
 * The axios instance (`@/utils/api`) already prepends `/api/v1` and carries
 * cookies for tenant + user auth, so routes here are mount-relative.
 *
 * Read-only. Voucher creation/posting/cancellation flows live elsewhere
 * (existing `LedgerAccountingPage.tsx`) and the explorer never mutates.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '@/utils/api';
import { profitLossSchema, type ProfitLossData } from './profitLossData';

// ─── Types ──────────────────────────────────────────────────────────────────

/** Full 20-value VoucherType enum + sentinel ones from pre-Sprint-2-3 AURA. */
export const VOUCHER_TYPES = [
  'SALES_INVOICE',
  'PURCHASE_INVOICE',
  'PAYMENT',
  'RECEIPT',
  'JOURNAL',
  'CONTRA',
  'BANK_ENTRY',
  'CASH_ENTRY',
  'CREDIT_CARD_ENTRY',
  'INTER_COMPANY',
  'EXCISE',
  'WRITE_OFF',
  'OPENING',
  'DEPRECIATION',
  'EXCHANGE_RATE_REVALUATION',
  'EXCHANGE_GAIN_LOSS',
  'DEFERRED_REVENUE',
  'DEFERRED_EXPENSE',
  'ITC_REVERSAL',
  'ITC_RECLAIM',
  // Legacy aliases still seen in older Voucher rows.
  'SALES',
  'PURCHASE',
  'CREDIT_NOTE',
  'DEBIT_NOTE',
  'MEMORANDUM',
] as const;

export type VoucherType = (typeof VOUCHER_TYPES)[number];

export type VoucherStatus = 'draft' | 'posted' | 'cancelled' | 'amended';

export interface VoucherLine {
  id?: string;
  voucherId?: string;
  /** FK to LedgerAccount. Schema name is `ledgerId`, NOT `accountId`. */
  ledgerId: string;
  ledgerName?: string;
  ledger?: { id: string; name: string; code?: string };
  debit: number | string;
  credit: number | string;
  narration?: string | null;

  // Sprint 2-3 multi-currency triplet (all optional, only populated when
  // the voucher itself is multi-currency).
  accountCurrency?: string | null;
  transactionCurrency?: string | null;
  transactionExchangeRate?: number | string | null;
  debitInAccountCurrency?: number | string | null;
  creditInAccountCurrency?: number | string | null;

  // Sprint 2-3 references — point at the source document (Sales Invoice,
  // PI, Payment, Bill of Entry, etc.). 17-doctype enum.
  referenceType?: string | null;
  referenceName?: string | null;

  // Audit / categorisation fields used in the detail drawer.
  valueDate?: string | null;
  fiscalYear?: string | null;
  against?: string | null;
  costCentreId?: string | null;
  projectId?: string | null;
  partyType?: string | null;
  partyId?: string | null;
  isTaxWithholdingAccount?: boolean | null;
  againstAccount?: string | null;
}

export interface Voucher {
  id: string;
  number: string;
  type: VoucherType | string;
  status: VoucherStatus | string;
  date: string;
  narration?: string | null;
  reference?: string | null;
  totalDebit: number | string;
  totalCredit: number | string;
  multiCurrency?: boolean | null;
  chequeNo?: string | null;
  chequeDate?: string | null;
  clearanceDate?: string | null;
  modeOfPayment?: string | null;
  fiscalYear?: string | null;
  postedBy?: string | null;
  postedAt?: string | null;
  cancelledBy?: string | null;
  cancelledAt?: string | null;
  amendedFrom?: string | null;
  reversalOf?: string | null;
  createdBy?: string | null;
  createdAt?: string | null;
  lines?: VoucherLine[];
}

export interface VoucherListParams {
  type?: string | string[];
  status?: string | string[];
  startDate?: string;
  endDate?: string;
  accountId?: string;
  partyId?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface UseVoucherListResult {
  vouchers: Voucher[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// ─── List hook ──────────────────────────────────────────────────────────────

export function useVoucherList(params: VoucherListParams): UseVoucherListResult {
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Stringify params to keep dep array stable.
  const key = JSON.stringify(params);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q: Record<string, unknown> = {};
      if (params.type) q.type = Array.isArray(params.type) ? params.type.join(',') : params.type;
      if (params.status)
        q.status = Array.isArray(params.status) ? params.status.join(',') : params.status;
      if (params.startDate) q.startDate = params.startDate;
      if (params.endDate) q.endDate = params.endDate;
      if (params.accountId) q.accountId = params.accountId;
      if (params.partyId) q.partyId = params.partyId;
      if (params.search) q.search = params.search;
      q.limit = params.limit ?? 500;
      if (params.offset) q.offset = params.offset;

      const { data } = await api.get('/ledger/vouchers', { params: q });
      setVouchers(Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load vouchers');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { vouchers, loading, error, refetch };
}

// ─── Detail hook ────────────────────────────────────────────────────────────

export function useVoucher(id: string | null | undefined) {
  const [voucher, setVoucher] = useState<Voucher | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      setVoucher(null);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    api
      .get(`/ledger/vouchers/${id}`)
      .then(({ data }) => {
        if (!active) return;
        setVoucher(data?.data ?? data ?? null);
      })
      .catch((e: any) => {
        if (!active) return;
        setError(e?.response?.data?.error || e?.message || 'Failed to load voucher');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  return { voucher, loading, error };
}

// ─── Ledger account dropdown options ────────────────────────────────────────

export interface LedgerAccountOption {
  id: string;
  name: string;
  code?: string | null;
  group?: { id: string; name: string; nature: string } | null;
}

export function useLedgerAccounts(search?: string) {
  const [options, setOptions] = useState<LedgerAccountOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api
      .get('/ledger/ledgers', { params: { limit: 500, search: search || undefined } })
      .then(({ data }) => {
        if (!active) return;
        setOptions(Array.isArray(data?.data) ? data.data : []);
      })
      .catch(() => {
        if (active) setOptions([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [search]);

  return { options, loading };
}

// ─── Account ledger drill-down ──────────────────────────────────────────────

export interface AccountLedgerRow {
  voucherId: string;
  voucherNumber: string;
  voucherType: string;
  date: string;
  debit: number | string;
  credit: number | string;
  narration?: string | null;
  runningBalance?: number | string;
}

export function useAccountLedger(
  accountId: string | null | undefined,
  startDate?: string,
  endDate?: string,
) {
  const [rows, setRows] = useState<AccountLedgerRow[]>([]);
  const [openingBalance, setOpeningBalance] = useState<number>(0);
  const [closingBalance, setClosingBalance] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accountId) {
      setRows([]);
      return;
    }
    let active = true;
    setLoading(true);
    setError(null);
    const params: Record<string, string> = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    api
      .get(`/ledger/${accountId}`, { params })
      .then(({ data }) => {
        if (!active) return;
        const payload = data?.data ?? data ?? {};
        setRows(Array.isArray(payload.rows) ? payload.rows : Array.isArray(payload) ? payload : []);
        setOpeningBalance(Number(payload.openingBalance) || 0);
        setClosingBalance(Number(payload.closingBalance) || 0);
      })
      .catch((e: any) => {
        if (!active) return;
        setError(e?.response?.data?.error || e?.message || 'Failed to load account ledger');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [accountId, startDate, endDate]);

  return { rows, openingBalance, closingBalance, loading, error };
}

// ─── Standard accounting reports ────────────────────────────────────────────

/**
 * Trial Balance / P&L / Balance Sheet — these target `/ledger/reports/*` on
 * the existing AURA backend (Sprint-2-3 left the report routes in place even
 * after re-mounting the voucher CRUD). If a future migration moves them to
 * `/ledger/trial-balance` etc, swap the path here.
 */
export function useTrialBalance(asOf?: string) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    api
      .get('/ledger/reports/trial-balance', { params: asOf ? { asOf } : {} })
      .then((res) => active && setData(res.data?.data ?? res.data ?? null))
      .catch(
        (e) =>
          active && setError(e?.response?.data?.error || e?.message || 'Failed to load trial balance'),
      )
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [asOf]);

  return { data, loading, error };
}

export function useProfitLoss(startDate?: string, endDate?: string) {
  const [data, setData] = useState<ProfitLossData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setData(null);
    setError(null);
    api.get('/ledger/reports/profit-loss', { params: {
      from: startDate ? `${startDate}T00:00:00+05:30` : undefined,
      to: endDate ? `${endDate}T23:59:59.999+05:30` : undefined,
    } }).then(res => {
      const parsed = profitLossSchema.safeParse(res.data?.data);
      if (!parsed.success) throw new Error('Invalid P&L response. Please retry.');
      if (active) setData(parsed.data);
    }).catch(e => {
      if (active) setError(e?.message || 'Failed to load P&L');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [startDate, endDate, attempt]);
  return { data, loading, error, retry: () => setAttempt(value => value + 1) };
}

export function useBalanceSheet(asOf?: string) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    api
      .get('/ledger/reports/balance-sheet', { params: asOf ? { asOf } : {} })
      .then((res) => active && setData(res.data?.data ?? res.data ?? null))
      .catch(
        (e) =>
          active && setError(e?.response?.data?.error || e?.message || 'Failed to load balance sheet'),
      )
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [asOf]);

  return { data, loading, error };
}
